import {
  CartActions,
  fillFullGearConfirmCopy,
  isIgFullGearEnabled,
  whenIgFullGearEnabled,
} from "@theme/cart-actions";
import { ThemeEvents } from "@theme/events";

const DECLINED_KEY = "cart_ladder_full_gear_declined";

const COPY = {
  upgrading: "Adding the full set…",
  cta: "I want it all",
  error: "We couldn't add Full Gear. Please try again.",
};

function fillConfirmCopy(confirm, row) {
  fillFullGearConfirmCopy(confirm, row);
}

function parseJsonScript(root, selector) {
  const script = root.querySelector(selector);
  if (!script?.textContent) return null;
  try {
    return JSON.parse(script.textContent);
  } catch {
    return null;
  }
}

function getConfirm() {
  return document.querySelector("[data-full-gear-confirm]");
}

function getRungThree() {
  return document.querySelector("cart-ladder-rung-three");
}

/** @type {Element | null} */
let pendingRemoveRow = null;

/**
 * @param {Element | null} [row]
 */
function showFullGearConfirm(row) {
  const confirm = getConfirm();
  if (!confirm) return;
  pendingRemoveRow = row || null;
  fillConfirmCopy(confirm, row);

  document.documentElement.setAttribute("data-ig-full-gear", "1");
  confirm.hidden = false;

  const scroller =
    confirm.closest(".cart-drawer__content") ||
    document.querySelector(".cart-drawer__content");
  if (scroller) {
    scroller.scrollTo({ top: 0, behavior: "smooth" });
  }
}

function hideFullGearConfirm() {
  const confirm = getConfirm();
  if (confirm) confirm.hidden = true;
}

function collectZuboGiftRemovals() {
  /** @type {{ id: string, quantity: number }[]} */
  const removals = [];
  document.querySelectorAll("[data-rung-2-zubo='1']").forEach((row) => {
    const key = row.getAttribute("data-key");
    if (key) removals.push({ id: key, quantity: 0 });
  });
  return removals;
}

function collectRungTwoRemovals() {
  /** @type {{ id: string, quantity: number }[]} */
  const removals = [...collectZuboGiftRemovals()];
  const seen = new Set(removals.map((item) => item.id));
  document.querySelectorAll("[data-rung-2-choice='1']").forEach((row) => {
    const key = row.getAttribute("data-key");
    if (!key || seen.has(key)) return;
    seen.add(key);
    removals.push({ id: key, quantity: 0 });
  });
  return removals;
}

function hasLadderChildren() {
  return Boolean(
    document.querySelector(
      "[data-rung-2-choice='1'], [data-rung-2-zubo='1'], [data-full-gear-added='1'], [data-full-gear-gift='1']",
    ),
  );
}

function collectFullGearRemovals(host) {
  const fromJson = host ? parseJsonScript(host, "[data-rung-three-removals]") : null;
  const removals = Array.isArray(fromJson) ? [...fromJson] : [];
  const seen = new Set(
    removals.map((item) => String(item?.id || "")).filter(Boolean),
  );

  document
    .querySelectorAll("[data-full-gear-added='1'], [data-full-gear-gift='1']")
    .forEach((row) => {
      const key = row.getAttribute("data-key");
      if (!key || seen.has(key)) return;
      seen.add(key);
      removals.push({ id: key, quantity: 0 });
    });

  return removals.filter((item) => item?.id);
}

/**
 * Strip Added-for-you knives + free Kezuriki, clear Full Gear, restore 2NEW.
 * Trash on a pre-ladder knife also removes rung 2 (choice + Zubo) and that knife.
 * Trash on the Zubo-chosen knife removes that knife + Zubo.
 * @param {Element | null} [host]
 */
function runFullGearDowngrade(host) {
  try {
    sessionStorage.removeItem(DECLINED_KEY);
  } catch {
    // sessionStorage can be blocked
  }

  const cartActions =
    host instanceof CartLadderRungThree
      ? host.cartActions
      : new CartActions();
  const removals = collectFullGearRemovals(host);
  const clicked = pendingRemoveRow;
  pendingRemoveRow = null;
  const clickedKey = clicked?.getAttribute("data-key") || "";
  const removeZuboChoice = clicked?.getAttribute("data-rung-2-choice") === "1";
  const removeBaseKnife = clicked?.getAttribute("data-ladder-base") === "1";

  if (removeBaseKnife && clickedKey) {
    removals.push({ id: clickedKey, quantity: 0 });
    removals.push(...collectRungTwoRemovals());
  } else if (removeZuboChoice && clickedKey) {
    removals.push({ id: clickedKey, quantity: 0 });
    removals.push(...collectZuboGiftRemovals());
  }

  /** @type {Record<string, number>} */
  const updates = {};
  for (const item of removals) {
    const id = String(item?.id || "");
    if (!id) continue;
    updates[id] = 0;
  }

  cartActions.updateCartState({
    ...(Object.keys(updates).length > 0 ? { updates } : {}),
    attributes: { _full_gear: "0" },
    discount: "2NEW",
  });
}

function interceptLadderRemove(event, row) {
  if (!row || !getConfirm()) return false;
  if (row.getAttribute("data-full-gear-gift") === "1") return false;

  const isPaid = row.getAttribute("data-full-gear-paid") === "1";
  const isBase =
    row.getAttribute("data-ladder-base") === "1" && hasLadderChildren();

  if (!isPaid && !isBase) return false;

  event.preventDefault();
  event.stopImmediatePropagation();
  showFullGearConfirm(row);
  return true;
}

document.addEventListener(
  "click",
  (event) => {
    if (!isIgFullGearEnabled()) return;

    const target = /** @type {HTMLElement | null} */ (event.target);

    if (target?.closest?.("[data-full-gear-keep]")) {
      event.preventDefault();
      pendingRemoveRow = null;
      hideFullGearConfirm();
      return;
    }

    if (target?.closest?.("[data-full-gear-downgrade]")) {
      event.preventDefault();
      hideFullGearConfirm();
      const host = getRungThree();
      if (host instanceof CartLadderRungThree) {
        host.downgrade();
      } else {
        runFullGearDowngrade(host);
      }
      return;
    }

    const minusBtn = target?.closest?.(".quantity-minus");
    const minusRow = minusBtn?.closest?.("tr[data-key]");
    if (minusBtn && minusRow) {
      const qtyInput = minusRow.querySelector('input[type="number"]');
      const qty = Number(
        qtyInput instanceof HTMLInputElement ? qtyInput.value : 1,
      );
      if (!Number.isFinite(qty) || qty <= 1) {
        if (interceptLadderRemove(event, minusRow)) return;
      }
    }

    const plusBtn = target?.closest?.(".quantity-plus");
    const addedRow = plusBtn?.closest?.("[data-full-gear-added='1']");
    if (plusBtn && addedRow) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const variantId = Number(addedRow.getAttribute("data-variant-id"));
      const host = getRungThree();
      if (variantId && host instanceof CartLadderRungThree) {
        host.addExtraKnife(variantId);
      }
      return;
    }

    const removeBtn = target?.closest?.(".cart-items__remove");
    if (!removeBtn) return;

    const row = removeBtn.closest("tr[data-key]");
    interceptLadderRemove(event, row);
  },
  true,
);

document.addEventListener("cart-ladder:confirm-remove", (event) => {
  const row =
    /** @type {CustomEvent<{ row?: Element | null }>} */ (event).detail?.row ||
    null;
  showFullGearConfirm(row);
});

document.addEventListener(
  "focusout",
  (event) => {
    if (!isIgFullGearEnabled()) return;
    const input = /** @type {HTMLElement | null} */ (event.target);
    if (!(input instanceof HTMLInputElement) || input.type !== "number") return;
    const row = input.closest("[data-full-gear-added='1']");
    if (!row) return;

    const next = Number(input.value);
    if (!Number.isFinite(next) || next <= 1) {
      input.value = "1";
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    input.value = "1";
    const variantId = Number(row.getAttribute("data-variant-id"));
    const host = getRungThree();
    if (variantId && host instanceof CartLadderRungThree) {
      host.addExtraKnife(variantId, next - 1);
    }
  },
  true,
);

class CartLadderRungThree extends HTMLElement {
  constructor() {
    super();
    this.cartActions = new CartActions();
    /** @type {HTMLButtonElement | null} */
    this.cta = null;
    /** @type {HTMLButtonElement | null} */
    this.decline = null;
    /** @type {HTMLElement | null} */
    this.status = null;
    /** @type {string} */
    this.ctaLabel = COPY.cta;
    this.handleCartError = this.handleCartError.bind(this);
  }

  connectedCallback() {
    this.cta = /** @type {HTMLButtonElement | null} */ (
      this.querySelector("[data-rung-three-cta]")
    );
    this.ctaLabel =
      this.cta?.textContent?.replace(/\s+/g, " ").trim() || COPY.cta;
    this.decline = /** @type {HTMLButtonElement | null} */ (
      this.querySelector("[data-rung-three-decline]")
    );
    this.status = /** @type {HTMLElement | null} */ (
      this.querySelector("[data-rung-three-status]")
    );

    document.addEventListener(ThemeEvents.cartError, this.handleCartError);

    const offer = this.querySelector("[data-rung-three-offer]");
    let declined = false;
    try {
      declined = sessionStorage.getItem(DECLINED_KEY) === "1";
    } catch {
      declined = false;
    }

    if (offer) {
      whenIgFullGearEnabled(() => {
        if (declined) {
          offer.hidden = true;
          this.unhideJulySpecial();
        } else {
          this.hideJulySpecial();
        }
      });
    }

    this.cta?.addEventListener("click", () => this.upgrade());
    this.decline?.addEventListener("click", () => this.onDecline());
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartError, this.handleCartError);
  }

  hideJulySpecial() {
    const drawer = this.closest(".cart-drawer__dialog");
    const julySpecial = /** @type {HTMLElement | null | undefined} */ (
      drawer?.querySelector(".cart-drawer-july-special")
    );
    if (julySpecial) julySpecial.hidden = true;
  }

  unhideJulySpecial() {
    const drawer = this.closest(".cart-drawer__dialog");
    const julySpecial = /** @type {HTMLElement | null | undefined} */ (
      drawer?.querySelector(".cart-drawer-july-special")
    );
    if (julySpecial) julySpecial.hidden = false;
  }

  onDecline() {
    try {
      sessionStorage.setItem(DECLINED_KEY, "1");
    } catch {
      // sessionStorage can be blocked; still hide for this render.
    }
    const offer = this.querySelector("[data-rung-three-offer]");
    if (offer) offer.hidden = true;
    this.unhideJulySpecial();
  }

  upgrade() {
    if (!isIgFullGearEnabled()) return;
    const items = parseJsonScript(this, "[data-rung-three-items]");
    if (!Array.isArray(items) || items.length === 0) return;

    this.setLoading(true);

    this.cartActions.upgradeFullGear(items, () => {
      document.removeEventListener(ThemeEvents.cartError, this.handleCartError);
    });
  }

  /**
   * Extra unit of an "Added for you" knife: new line, no Full Gear properties.
   * @param {number} variantId
   * @param {number} [quantity]
   */
  addExtraKnife(variantId, quantity = 1) {
    if (!variantId) return;
    const qty = Math.max(1, Number(quantity) || 1);
    this.cartActions.addItems([{ id: variantId, quantity: qty }]);
  }

  downgrade() {
    hideFullGearConfirm();
    runFullGearDowngrade(this);
  }

  /**
   * @param {boolean} isLoading
   */
  setLoading(isLoading) {
    if (!this.cta) return;
    this.cta.disabled = isLoading;
    this.cta.textContent = isLoading ? COPY.upgrading : this.ctaLabel || COPY.cta;
    this.setAttribute("aria-busy", isLoading ? "true" : "false");
    if (this.decline) this.decline.disabled = isLoading;
  }

  /**
   * @param {Event} event
   */
  handleCartError(event) {
    const cartError = /** @type {CustomEvent} */ (event);
    const source = cartError.detail?.sourceId;
    if (
      source !== "cart_actions_add_items" &&
      source !== "cart_actions_update_items" &&
      source !== "cart_actions_update_attributes"
    ) {
      return;
    }
    if (!this.status) return;

    this.setLoading(false);
    this.status.textContent =
      cartError.detail?.data?.description ||
      cartError.detail?.data?.message ||
      COPY.error;
  }
}

if (!customElements.get("cart-ladder-rung-three")) {
  customElements.define("cart-ladder-rung-three", CartLadderRungThree);
}
